import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { LoadingButton } from "@/components/LoadingButton";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Toggle } from "@/components/ui/toggle";
import { useQuery } from "@tanstack/react-query";
import { productQueries, type Product } from "@/api/product";
import { createProduct, updateProduct } from "@/api/product";

const productNameRegex = /^[a-z0-9-]+$/;

const automationConfigSchema = z.object({
  showFrequency: z.boolean().default(true),
  showActions: z.boolean().default(true),
  showVolume: z.boolean().default(true),
  actionLabel: z.string().default(""),
  volumeLabel: z.string().default(""),
});

const productSchema = z.object({
  product_family: z
    .string()
    .min(1, "Product family is required")
    .regex(/^[a-z0-9-]+$/, {
      message: "Family name must be lowercase, numbers and hyphens only",
    }),
  product_id: z
    .string()
    .min(1, "ID is required")
    .regex(productNameRegex, "Name must be lowercase, numbers and hyphens only")
    .transform((val) => val.toLowerCase()),
  name: z.string().min(1, "Name is required"),
  credit_cost: z
    .number()
    .min(1, "Minimum credit cost is 1")
    .transform((val) => Number(val.toFixed(6))),
  description: z.string().min(1, "Description is required"),
  active: z.boolean().default(true),
  product_lifecycle: z.enum(
    ["internal", "private_preview", "public_preview", "ga"],
    {
      required_error: "Please select a lifecycle stage",
    },
  ),
  metadata: z.record(z.any()).optional(),
  automation_product: z.boolean().default(false),
  estimated_cost: z.number().min(0, "Minimum cost is 0").default(0),
  product_owner: z.string().optional(),
  automation_config: automationConfigSchema.default({
    showFrequency: true,
    showActions: true,
    showVolume: true,
    actionLabel: "",
    volumeLabel: "",
  }),
});

type ProductFormData = z.infer<typeof productSchema>;

interface ProductDialogProps {
  product: Product | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ProductDialog({
  product,
  open,
  onClose,
  onSuccess,
}: ProductDialogProps) {
  const { data: families = [] } = useQuery(productQueries.productFamilies());
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState<ProductFormData | null>(null);
  const [isNewFamily, setIsNewFamily] = useState(false);

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      product_family: "",
      name: "",
      product_id: "",
      credit_cost: 1,
      description: "",
      active: true,
      product_lifecycle: "internal",
      metadata: {},
      automation_product: false,
      estimated_cost: 0,
      product_owner: "",
      automation_config: {
        showFrequency: true,
        showActions: true,
        showVolume: true,
        actionLabel: "",
        volumeLabel: "",
      },
    },
  });

  useEffect(() => {
    if (product) {
      form.reset({
        product_family: product.product_family,
        name: product.name,
        product_id: product.product_id,
        credit_cost: product.credit_cost,
        description: product.description,
        active: product.active,
        product_lifecycle: product.product_lifecycle,
        metadata: product.metadata || {},
        automation_product: product.automation_product ?? false,
        estimated_cost: product.estimated_cost ?? 0,
        product_owner: product.product_owner ?? "",
        automation_config: product.automation_config ?? {
          showFrequency: true,
          showActions: true,
          showVolume: true,
          actionLabel: "",
          volumeLabel: "",
        },
      });
    }
  }, [product, form]);

  const handleSubmit = async (data: ProductFormData) => {
    setFormData(data);
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    if (!formData) return;

    try {
      if (product) {
        await updateProduct(product.product_sku, formData);
      } else {
        await createProduct(formData);
      }
      setShowConfirmation(false);
      onSuccess();
    } catch (error) {
      console.error("Error saving product:", error);
      setShowConfirmation(false);
    }
  };

  const getProductSku = (family: string, name: string) => {
    return `${family}-${name}`.toLowerCase();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={() => onClose()}>
        <DialogContent className="w-full max-w-screen-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {product ? "Edit Product" : "Create New Product"}
            </DialogTitle>
            <DialogDescription>
              {product
                ? "Update the product details below."
                : "Add a new product to your credit system."}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-6"
            >
              {/* Basic Information Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Product Family Field */}
                  <FormField
                    control={form.control}
                    name="product_family"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Product Family</FormLabel>
                        {!isNewFamily ? (
                          <>
                            <Select
                              onValueChange={(value) => {
                                if (value === "new") {
                                  setIsNewFamily(true);
                                  field.onChange("");
                                } else {
                                  field.onChange(value);
                                }
                              }}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select or create a product family" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {families.map((family) => (
                                  <SelectItem key={family} value={family}>
                                    {family}
                                  </SelectItem>
                                ))}
                                <SelectItem
                                  value="new"
                                  className="text-primary"
                                >
                                  + Create New Family
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Select an existing family or create a new one
                            </FormDescription>
                          </>
                        ) : (
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <FormControl>
                                <Input
                                  placeholder="Enter new family name"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(e.target.value.toLowerCase())
                                  }
                                  value={field.value}
                                />
                              </FormControl>
                              <FormDescription>
                                Use lowercase letters, numbers, and hyphens
                              </FormDescription>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setIsNewFamily(false);
                                field.onChange("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Product Name Field */}
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Product Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="enter-product-name"
                            {...field}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        </FormControl>
                        <FormDescription>Public product name</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Product ID Field */}
                  <FormField
                    control={form.control}
                    name="product_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Product ID </FormLabel>
                        <FormControl>
                          <Input placeholder="custom-id" {...field} />
                        </FormControl>
                        <FormDescription>
                          Use lower cases letters and numbers, no spaces
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Credit Cost Field */}
                  <FormField
                    control={form.control}
                    name="credit_cost"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Credit Cost</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="1"
                            min="1"
                            placeholder="Enter credit cost"
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseFloat(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormDescription>
                          Credit cost per API call (minimum 1 credit)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Description Field */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter product description"
                          className="min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Product Status Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Product Status</h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Lifecycle Stage Field */}
                  <FormField
                    control={form.control}
                    name="product_lifecycle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Product Lifecycle Stage</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select lifecycle stage" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="internal">Internal</SelectItem>
                            <SelectItem value="private_preview">
                              Private Preview
                            </SelectItem>
                            <SelectItem value="public_preview">
                              Public Preview
                            </SelectItem>
                            <SelectItem value="ga">
                              GA (General Availability)
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Set the current lifecycle stage
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Active Status Toggle */}
                  <FormField
                    control={form.control}
                    name="active"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Product Status</FormLabel>
                          <FormDescription>
                            Enable or disable API calls
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Toggle
                            pressed={field.value}
                            onPressedChange={field.onChange}
                            variant="outline"
                          >
                            {field.value ? "Active" : "Inactive"}
                          </Toggle>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Automation Settings Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Automation Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Automation Product Toggle */}
                  <FormField
                    control={form.control}
                    name="automation_product"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Automation Product</FormLabel>
                          <FormDescription>
                            Enable automation features
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Toggle
                            pressed={field.value}
                            onPressedChange={field.onChange}
                            variant="outline"
                          >
                            {field.value ? "Enabled" : "Disabled"}
                          </Toggle>
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Estimated Cost Field */}
                  <FormField
                    control={form.control}
                    name="estimated_cost"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estimated Cost (USD)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseFloat(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormDescription>
                          Approximate cost per operation
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Automation Config Section */}
                <div className="space-y-4 bg-secondary/10 p-4 rounded-lg">
                  <h4 className="font-medium">Display Configuration</h4>

                  {/* Toggle Controls */}
                  <div className="grid grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="automation_config.showFrequency"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between space-x-2">
                          <FormLabel>Show Frequency</FormLabel>
                          <FormControl>
                            <Toggle
                              pressed={field.value}
                              onPressedChange={field.onChange}
                              size="sm"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="automation_config.showActions"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between space-x-2">
                          <FormLabel>Show Actions</FormLabel>
                          <FormControl>
                            <Toggle
                              pressed={field.value}
                              onPressedChange={field.onChange}
                              size="sm"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="automation_config.showVolume"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between space-x-2">
                          <FormLabel>Show Volume</FormLabel>
                          <FormControl>
                            <Toggle
                              pressed={field.value}
                              onPressedChange={field.onChange}
                              size="sm"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Label Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="automation_config.actionLabel"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Action Label</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Actions per Day" />
                          </FormControl>
                          <FormDescription>
                            Label for action metrics
                          </FormDescription>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="automation_config.volumeLabel"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Volume Label</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Estimated Daily Volume"
                            />
                          </FormControl>
                          <FormDescription>
                            Label for volume metrics
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Additional Information Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">
                  Additional Information
                </h3>
                <FormField
                  control={form.control}
                  name="product_owner"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Product Owner</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="email@example.com" />
                      </FormControl>
                      <FormDescription>
                        Primary contact for this product
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={onClose} type="button">
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  loading={form.formState.isSubmitting}
                >
                  {product ? "Update" : "Create"}
                </LoadingButton>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Product Details</AlertDialogTitle>
            <AlertDialogDescription>
              Please review the product details below:
              <div className="mt-4 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <span className="font-medium">Product SKU:</span>
                  <span className="col-span-2 font-mono bg-secondary/30 px-2 rounded">
                    {formData &&
                      getProductSku(formData.product_family, formData.name)}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <span className="font-medium">Family:</span>
                  <span className="col-span-2">{formData?.product_family}</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <span className="font-medium">Name:</span>
                  <span className="col-span-2">{formData?.name}</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <span className="font-medium">Product ID:</span>
                  <span className="col-span-2">
                    {formData?.product_id || formData?.name}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <span className="font-medium">Credit Cost:</span>
                  <span className="col-span-2">
                    {formData?.credit_cost} credits
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <span className="font-medium">Lifecycle Stage:</span>
                  <span className="col-span-2">
                    {formData?.product_lifecycle
                      .split("_")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(" ")}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <span className="font-medium">Status:</span>
                  <span className="col-span-2">
                    {formData?.active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <span className="font-medium">Automation:</span>
                  <span className="col-span-2">
                    {formData?.automation_product ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <span className="font-medium">Estimated Cost:</span>
                  <span className="col-span-2">
                    ${formData?.estimated_cost.toFixed(2)}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <span className="font-medium">Owner:</span>
                  <span className="col-span-2">
                    {formData?.product_owner || "—"}
                  </span>
                </div>
                {/* Automation Config Summary */}
                <div className="grid grid-cols-3 gap-2 border-t pt-2 mt-2">
                  <span className="font-medium">Display Settings:</span>
                  <span className="col-span-2">
                    <ul className="list-disc list-inside">
                      <li>
                        Frequency:{" "}
                        {formData?.automation_config.showFrequency
                          ? "Show"
                          : "Hide"}
                      </li>
                      <li>
                        Actions:{" "}
                        {formData?.automation_config.showActions
                          ? "Show"
                          : "Hide"}
                      </li>
                      <li>
                        Volume:{" "}
                        {formData?.automation_config.showVolume
                          ? "Show"
                          : "Hide"}
                      </li>
                    </ul>
                  </span>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmation(false)}
            >
              Back to Edit
            </Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
